import { ContentTag, Currency } from '@round/api';
import React, { CSSProperties, useMemo } from 'react';
import ClipboardTable from 'utility/copyToClipboard/ClipboardTable/ClipboardTable';
import { Column } from 'react-table';
import {
    asMoney,
    buildTiktokUserUrl,
    displayOptionalNumericTableValue,
    formatNumberToKNotation,
} from '../../../../helpers';

export type TikTokProposalClipboardTableRow = {
    account: string;
    followerCount: number | null;
    estimatedViews: number | null;
    location: string | null;
    contentTags: ContentTag[];
    cost: number | null;
};

type Props = {
    items: TikTokProposalClipboardTableRow[];
    campaignName: string;
    caption: string;
    brief: string;
    currency: Currency | undefined;
    includeCost?: boolean;
};

const tfootStyles: CSSProperties = {
    background: '#eeeeee',
    fontWeight: 'bold',
};

const tdStyles: CSSProperties = {
    border: '1px solid black',
    padding: '5px',
};

const TikTokProposalClipboardTable = ({ items, campaignName, brief, caption, currency, includeCost = true }: Props) => {
    const columns: Column<TikTokProposalClipboardTableRow>[] = useMemo(
        () => [
            {
                Header: () => <div style={{ textAlign: 'left' }}>{campaignName}</div>,
                accessor: 'account',
                Cell: ({ value, row: { original } }) => {
                    if (!value) {
                        return <>-</>;
                    }

                    const accountUrl = buildTiktokUserUrl(value);

                    return (
                        <div>
                            <a href={accountUrl} rel="noopener noreferrer" target="_blank">
                                {accountUrl}
                            </a>

                            {typeof original.followerCount === 'number' && (
                                <span style={{ marginLeft: '5px' }}>
                                    ({formatNumberToKNotation(original.followerCount)})
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: 'Cost',
                accessor: 'cost',
                Cell: ({ value }) => {
                    return <div style={{ textAlign: 'center' }}>{asMoney(value, currency)}</div>;
                },
            },
            {
                Header: 'Est. views',
                accessor: 'estimatedViews',
                Cell: ({ value }) => (
                    <div style={{ textAlign: 'center' }}>{displayOptionalNumericTableValue(value)}</div>
                ),
            },
            {
                Header: 'Country',
                accessor: 'location',
                Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value ?? '-'}</div>,
            },
            {
                Header: 'Content',
                accessor: 'contentTags',
                Cell: ({ value }) => <>{value.map((t) => t.name).join(', ')}</>,
            },
            {
                Header: 'Brief',
                id: 'brief',
                Cell: () => <pre style={{ fontFamily: 'inherit' }}>{brief}</pre>,
            },
        ],
        [brief, campaignName, currency]
    );

    const totalEstimatedViews = useMemo(() => {
        if (items.some((item) => typeof item.estimatedViews === 'number')) {
            return items.reduce((acc, item) => acc + (item.estimatedViews ?? 0), 0);
        }

        return null;
    }, [items]);

    const totalCost = useMemo(() => {
        if (items.some((item) => typeof item.cost === 'number')) {
            return items.reduce((acc, item) => acc + (item.cost ?? 0), 0);
        }

        return null;
    }, [items]);

    return (
        <ClipboardTable
            columns={columns.filter((c) => includeCost || c.accessor !== 'cost')}
            data={items}
            caption={caption}
            renderRow={(row, rowIndex, rowsArray) => {
                return (
                    <tr {...row.getRowProps()}>
                        {row.cells.map((cell, index) => {
                            const isBriefCell = cell.column.id === 'brief';
                            if (isBriefCell && rowIndex > 0) {
                                return null;
                            }

                            return (
                                <td
                                    {...cell.getCellProps()}
                                    colSpan={index > 0 ? 1 : 2}
                                    rowSpan={isBriefCell && rowIndex === 0 ? rowsArray.length : 1}
                                    style={tdStyles}
                                >
                                    {cell.render('Cell')}
                                </td>
                            );
                        })}
                    </tr>
                );
            }}
            renderFooter={() => (
                <tfoot style={tfootStyles}>
                    <tr>
                        <td style={tdStyles} colSpan={2} />
                        {includeCost && (
                            <td style={tdStyles}>
                                {typeof totalCost === 'number' ? asMoney(totalCost, currency) : '-'}
                            </td>
                        )}
                        <td style={tdStyles}>{displayOptionalNumericTableValue(totalEstimatedViews)}</td>
                        <td style={tdStyles} colSpan={4} />
                    </tr>
                </tfoot>
            )}
        />
    );
};

export default TikTokProposalClipboardTable;
